import { motion } from "framer-motion";
import { GraduationCap, Plane, Globe2, ArrowRight } from "lucide-react";
import FlightTicket from "../assets/flight-ticket.jpg";
import MigrationService from "../assets/booking.jpg";
import StudyAbroad from "../assets/graduates.jpg";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/Button";
import { openWhatsApp } from "../utils/whatsapp";

const services = [
  {
    title: "Study Abroad Services",
    description:
      "We provide comprehensive support for students aspiring to pursue their education abroad, guiding you through every step of your academic journey.",
    image: StudyAbroad,
    slug: "study-abroad",
    icon: <GraduationCap className="w-8 h-8" />,
    color: "#800020",
    features: [
      "Program selection and guidance",
      "University applications",
      "Scholarship support",
      "Visa processing",
      "Pre-departure briefing",
    ],
  },
  {
    title: "Migration Services",
    slug: "migration",
    description:
      "Navigate the complexities of immigration with our expert guidance, helping you achieve your migration goals with confidence and ease.",
    image: FlightTicket,
    icon: <Globe2 className="w-8 h-8" />,
    color: "#800020",
    features: [
      "Immigration consultation",
      "Visa eligibility assessment",
      "Permanent residency pathways",
      "Work visa processing",
      "Documentation support",
    ],
  },
  {
    title: "Flight Ticket Booking",
    description:
      "Experience hassle-free travel booking with competitive prices and personalized service tailored to your travel needs.",
    image: MigrationService,
    icon: <Plane className="w-8 h-8" />,
    color: "#800020",
    slug: "flight-booking",
    features: [
      "Local & international flights",
      "Best-price ticket search",
      "Group travel support",
      "Student travel discounts",
    ],
  },
];

export default function ServiceSection() {
  const navigate = useNavigate(); // Add navigate hook

  // Handle CTA by opening WhatsApp with service context
  const handleLearnMore = (slug: string) => {
    const service = services.find((s) => s.slug === slug);
    openWhatsApp(
      `Hello! I am interested in ${service?.title ?? "your services"}. Please help me get started.\n\nName:\nEmail:\nPhone:`,
    );
  };
  return (
    <section
      className="py-20 bg-gradient-to-br from-gray-50 to-gray-100"
      content-visibility="auto"
      contain-intrinsic-size="1200px"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-poppins text-[#800020] font-semibold tracking-tight uppercase text-sm mb-2 block antialiased">
            Our Services
          </span>
          <h2 className="font-poppins text-4xl md:text-5xl font-bold text-gray-900 tracking-tight leading-tight antialiased mb-4">
            Everything You Need in One Place
          </h2>
          <p className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto tracking-tight leading-relaxed antialiased">
            From study abroad guidance to migration services and flight
            bookings, we're your complete solution for global opportunities.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${
                index % 2 === 0 ? "" : ""
              }`}
            >
              <div
                className={`grid lg:grid-cols-2 gap-0 ${
                  index % 2 === 1 ? "lg:grid-flow-dense" : ""
                }`}
              >
                {/* Content Side */}
                <div
                  className={`p-8 lg:p-12 flex flex-col justify-center ${
                    index % 2 === 1 ? "lg:col-start-2" : ""
                  }`}
                >
                  {/* Icon */}
                  <div
                    className="inline-flex w-16 h-16 items-center justify-center rounded-2xl mb-6"
                    style={{
                      backgroundColor: `${service.color}15`,
                      color: service.color,
                    }}
                  >
                    {service.icon}
                  </div>

                  {/* Title */}
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight leading-tight antialiased">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="font-jakarta text-gray-600 text-lg mb-6 tracking-tight leading-relaxed antialiased">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start font-jakarta text-gray-700 tracking-tight antialiased"
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0"
                          style={{ backgroundColor: service.color }}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    className="self-start"
                    style={{ backgroundColor: service.color }}
                    onClick={() => handleLearnMore(service.slug)}
                    rightIcon={<ArrowRight className="w-5 h-5" />}
                  >
                    Learn More
                  </Button>
                </div>

                {/* Image Side */}
                <div
                  className={`relative h-64 lg:h-auto ${
                    index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""
                  }`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="600"
                    height="400"
                  />
                  <div
                    className="absolute inset-0 opacity-20"
                    style={{
                      background: `linear-gradient(135deg, ${service.color} 0%, transparent 100%)`,
                    }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
